import { useCallback, useContext, useRef, useMemo } from 'react';
import {
  useMeetingSelector,
  useStableEvent
} from '../meeting/meetingProviderContextDef';
import {
  useCharacterTrackedState,
  CharacterLiveStateContext,
  CHARACTER_STATE_KEYS,
  EMPTY_CHARACTER_STATE
} from './characterStateContext';
import { events, rejectUnavailable } from '../utils';

const NOOP = () => {};

/**
 *
 * @param {
 * {
 *  interactionId?: string,
 *  id?: string,
 *  displayName?: string,
 *  characterRole?: string,
 *  characterMode?: "text" | "co_pilot" | "auto_pilot" | "vision_pilot",
 *  knowledgeBases?: string[],
 *  language?: string,
 * },
 * {
 *  onCharacterMessage?: Function,
 *  onData?: Function,
 *  onError?: Function,
 *  onUserMessage?: Function,
 *  onStreamDisabled?: Function,
 *  onStreamEnabled?: Function,
 *  onMediaStatusChanged?: Function,
 *  onVideoQualityChanged?: Function,
 * }} options
 *
 * @returns {
 *
 *  displayName: string,
 *  webcamStream: Stream,
 *  micStream: Stream,
 *  webcamOn: boolean,
 *  micOn: boolean,
 *  isActiveSpeaker: string,
 *
 *  enableMic: Function,
 *  disableMic: Function,
 *  enableWebcam: Function,
 *  disableWebcam: Function,
 *
 *  join: Function,
 *  leave: Function,
 *  sendMessage: Function
 *  interrupt: Function
 *
 *}
 *
 */
const useCharacter = (
  {
    interactionId,
    id,
    characterMode,
    displayName,
    characterRole,
    knowledgeBases,
    language,
    metaData
  },
  {
    onCharacterStateChanged = NOOP,
    onCharacterMessage = NOOP,
    onUserMessage = NOOP,
    onData = NOOP,
    onError = NOOP,
    onCharacterJoined = NOOP,
    onCharacterLeft = NOOP,
    onStreamEnabled = NOOP,
    onStreamDisabled = NOOP,
    onMediaStatusChanged = NOOP,
    onVideoQualityChanged = NOOP
  } = {}
) => {
  const allState = useCharacterTrackedState();
  const liveStateRef = useContext(CharacterLiveStateContext);
  const meeting = useMeetingSelector(s => s.meeting);
  const characters = useMeetingSelector(s => s.characters);

  const character = useMemo(() => {
    if (!meeting) return null;
    if (characters) {
      if (interactionId) {
        for (const c of characters.values()) {
          if (c.interactionId == interactionId) return c;
        }
      }
      if (id) {
        const found = characters.get(id);
        if (found) return found;
      }
    }
    if (typeof meeting.createCharacter === 'function') {
      return meeting.createCharacter({
        interactionId,
        displayName,
        id,
        characterRole,
        characterMode,
        knowledgeBases,
        language,
        metaData
      });
    }
    return null;
  }, [
    meeting,
    characters,
    interactionId,
    id,
    characterMode,
    displayName,
    characterRole,
    knowledgeBases,
    language,
    metaData
  ]);

  const characterRef = useRef(character);
  characterRef.current = character;

  const characterId = character?.id;

  useStableEvent(
    `${events['character-state-changed']}-${characterId}`,
    onCharacterStateChanged
  );
  useStableEvent(
    `${events['character-message']}-${characterId}`,
    onCharacterMessage
  );
  useStableEvent(`${events['user-message']}-${characterId}`, onUserMessage);
  useStableEvent(`${events['character-data']}-${characterId}`, onData);
  useStableEvent(`${events['character-error']}-${characterId}`, onError);
  useStableEvent(
    `${events['character-joined']}-${characterId}`,
    onCharacterJoined
  );
  useStableEvent(`${events['character-left']}-${characterId}`, onCharacterLeft);
  useStableEvent(
    `${events['participant-stream-enabled']}-${characterId}`,
    onStreamEnabled
  );
  useStableEvent(
    `${events['participant-stream-disabled']}-${characterId}`,
    onStreamDisabled
  );
  useStableEvent(
    `${events['participant-media-status-changed']}-${characterId}`,
    onMediaStatusChanged
  );
  useStableEvent(
    `${events['participant-video-quality-changed']}-${characterId}`,
    onVideoQualityChanged
  );

  const enableMic = useCallback(() => {
    if (!characterRef.current) {
      return rejectUnavailable('enableMic', 'character');
    }
    return characterRef.current.enableMic();
  }, []);
  const disableMic = useCallback(() => {
    if (!characterRef.current) {
      return rejectUnavailable('disableMic', 'character');
    }
    return characterRef.current.disableMic();
  }, []);
  const enableWebcam = useCallback(() => {
    if (!characterRef.current) {
      return rejectUnavailable('enableWebcam', 'character');
    }
    return characterRef.current.enableWebcam();
  }, []);
  const disableWebcam = useCallback(() => {
    if (!characterRef.current) {
      return rejectUnavailable('disableWebcam', 'character');
    }
    return characterRef.current.disableWebcam();
  }, []);
  const join = useCallback(() => {
    if (!characterRef.current) {
      return rejectUnavailable('join', 'character');
    }
    return characterRef.current.join();
  }, []);
  const leave = useCallback(() => {
    if (!characterRef.current) {
      return rejectUnavailable('leave', 'character');
    }
    return characterRef.current.leave();
  }, []);
  const sendMessage = useCallback(d => {
    if (!characterRef.current) {
      return rejectUnavailable('sendMessage', 'character');
    }
    return characterRef.current.sendMessage(d);
  }, []);
  const interrupt = useCallback(() => {
    if (!characterRef.current) {
      return rejectUnavailable('interrupt', 'character');
    }
    return characterRef.current.interrupt();
  }, []);

  const methodsRef = useRef(null);
  methodsRef.current = {
    enableMic,
    disableMic,
    enableWebcam,
    disableWebcam,
    join,
    leave,
    sendMessage,
    interrupt
  };

  return useMemo(
    () =>
      new Proxy(allState, {
        get(target, prop, receiver) {
          if (prop in CHARACTER_STATE_KEYS) {
            const cState = characterId ? target[characterId] : null;
            const trackedValue = cState
              ? cState[prop]
              : EMPTY_CHARACTER_STATE[prop];
            const live = liveStateRef && liveStateRef.current;
            const liveCState = live && characterId ? live[characterId] : null;
            return liveCState ? liveCState[prop] : trackedValue;
          }
          const m = methodsRef.current;
          if (m && prop in m) {
            if (!characterRef.current) {
              return () => {
                console.warn(
                  `useCharacter: ${String(
                    prop
                  )}() called but character not found.`
                );
              };
            }
            return m[prop];
          }
          return Reflect.get(target, prop, receiver);
        },
        has(target, prop) {
          return (
            prop in CHARACTER_STATE_KEYS ||
            !!(methodsRef.current && prop in methodsRef.current)
          );
        },
        ownKeys() {
          const stateKeys = Object.keys(CHARACTER_STATE_KEYS);
          const mKeys = Object.keys(methodsRef.current ?? {});
          return [...new Set([...stateKeys, ...mKeys])];
        },
        getOwnPropertyDescriptor() {
          return { configurable: true, enumerable: true, writable: false };
        }
      }),
    [allState, characterId, liveStateRef]
  );
};

export default useCharacter;
